import React, { useMemo } from 'react';
import { computeUsageSavings, computePayback } from '../calculations/roiCalculations.js';
import { computeOpex } from '../calculations/opexCalculations.js';
import { fmtEUR } from '../calculations/budgetCalculations.js';

/**
 * Étape 7 : retour sur investissement, à partir de la cuve choisie à l'étape 5 (volume +
 * couverture/fiabilité) et du budget estimé à l'étape 6. Le prix de l'eau est décomposé en
 * distribution/production et assainissement, car seule la part distribution est évitée pour
 * les usages raccordés à l'égout (cf. colonne "Égout" de l'étape 1). L'OPEX annuel (électricité
 * pompage, consommables de filtration, dérivé des choix faits à l'étape Budget) est déduit au
 * point médian de l'économie annuelle avant calcul du payback — affiché en un seul poste agrégé.
 */
const RoiStep = ({
  usages,
  selectedTankVolume, selectedTankCoverage,
  budgetState,
  budgetTotalLow, budgetTotalHigh,
  waterPrice, setWaterPrice,
  currentStep, totalSteps, goPrevious,
}) => {
  const coverage = selectedTankCoverage ?? 0;
  const priceTotal = waterPrice.dist + waterPrice.sewage;

  const { rows, totalWaterSavedM3, totalEurosSaved } = useMemo(
    () => computeUsageSavings(usages, coverage, waterPrice.dist, waterPrice.sewage),
    [usages, coverage, waterPrice.dist, waterPrice.sewage]
  );

  const opex = useMemo(() => computeOpex(budgetState), [budgetState]);
  const opexMid = (opex.low + opex.high) / 2;
  const netEurosSaved = totalEurosSaved - opexMid;

  const payback = computePayback(netEurosSaved, budgetTotalLow, budgetTotalHigh);
  const avgBudget = (budgetTotalLow + budgetTotalHigh) / 2;

  return (
    <div className="apv2-step-card">
      <div className="apv2-step-header">
        <div className="apv2-step-header-text">
          <span className="apv2-step-eyebrow">Récupération d'eau de pluie</span>
          <h2 className="apv2-step-heading">Retour sur investissement</h2>
        </div>
        <span className="apv2-step-counter">{currentStep + 1} / {totalSteps}</span>
      </div>
      <div className="apv2-step-body">

        <p className="apv2-sim-hint">Repris de la cuve choisie à l'étape 5 et du budget estimé à l'étape 6.</p>
        <div className="apv2-summary-cards">
          <div className="apv2-summary-card">
            <span className="apv2-summary-value">{selectedTankVolume != null ? `${selectedTankVolume.toFixed(2)} m³` : '—'}</span>
            <span className="apv2-summary-label">cuve retenue</span>
          </div>
          <div className="apv2-summary-card">
            <span className="apv2-summary-value">{selectedTankCoverage != null ? `${(selectedTankCoverage * 100).toFixed(1)}%` : '—'}</span>
            <span className="apv2-summary-label">couverture estimée</span>
          </div>
          <div className="apv2-summary-card apv2-summary-card--secondary">
            <span className="apv2-summary-value">{fmtEUR(avgBudget)} €</span>
            <span className="apv2-summary-label">budget estimé (moyenne : {fmtEUR(budgetTotalLow)}–{fmtEUR(budgetTotalHigh)} €)</span>
          </div>
        </div>
        {selectedTankVolume == null && (
          <p className="apv2-sim-hint" style={{ marginTop: 12 }}>
            Aucune cuve n'a encore été choisie à l'étape 5 (bouton « Choisir cette cuve » dans le tableau).
            En attendant, les calculs ci-dessous utilisent une couverture de 0%.
          </p>
        )}

        <div className="rw-sub-label" style={{ marginTop: 24 }}>Prix de l'eau</div>
        <p className="apv2-sim-hint">
          Le prix de l'eau se compose généralement de deux parts sur la facture : la <b>production/distribution</b> (l'eau
          potable elle-même) et l'<b>assainissement</b> (collecte + traitement des eaux usées). Pour un usage raccordé à
          l'égout (coché « Égout » à l'étape 1), utiliser de l'eau de pluie n'évite que la part distribution : l'eau usée
          part quand même au traitement, donc l'assainissement reste dû (généralement via le compteur volumétrique déclaré
          à la collectivité). Pour un usage non raccordé (jardin, lavage extérieur...), les deux parts sont économisées.
        </p>
        <div className="rw-param-row">
          <div className="rw-param-field">
            <label>Production / distribution (€/m³)</label>
            <input
              type="number" step="0.1" min="0"
              value={waterPrice.dist}
              onChange={e => setWaterPrice(prev => ({ ...prev, dist: +e.target.value || 0 }))}
            />
          </div>
          <div className="rw-param-field">
            <label>Assainissement (€/m³)</label>
            <input
              type="number" step="0.1" min="0"
              value={waterPrice.sewage}
              onChange={e => setWaterPrice(prev => ({ ...prev, sewage: +e.target.value || 0 }))}
            />
          </div>
          <div className="rw-param-field">
            <label>Total (€/m³)</label>
            <input type="number" value={priceTotal.toFixed(2)} disabled />
          </div>
        </div>

        <div className="rw-sub-label" style={{ marginTop: 24 }}>Économies et retour sur investissement</div>
        <p className="apv2-sim-hint">
          Pour chaque usage actif (étape 1) : eau économisée = volume annuel × couverture de la cuve choisie (étape 5).
          Le tarif appliqué dépend de la case « Égout » cochée à l'étape 1.
        </p>

        <div className="apv2-flat-table-wrapper">
          <table className="apv2-flat-table">
            <thead>
              <tr><th>Usage</th><th>Volume annuel</th><th>Égout ?</th><th>Eau économisée</th><th>Tarif appliqué</th><th>Économie</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6}>Aucun usage actif à l'étape 1.</td></tr>
              ) : rows.map(r => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td>{r.volume.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} m³</td>
                  <td>{r.sewage ? 'Oui' : 'Non'}</td>
                  <td>{r.savedM3.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} m³</td>
                  <td>{r.rate.toFixed(2)} €/m³ {r.sewage ? '(distrib. seule)' : '(distrib. + assain.)'}</td>
                  <td>{fmtEUR(r.eurosSaved)} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="apv2-summary-cards" style={{ marginTop: 16 }}>
          <div className="apv2-summary-card">
            <span className="apv2-summary-value">{totalWaterSavedM3.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} m³</span>
            <span className="apv2-summary-label">eau économisée (total) / an</span>
          </div>
          <div className="apv2-summary-card">
            <span className="apv2-summary-value">{fmtEUR(totalEurosSaved)} €</span>
            <span className="apv2-summary-label">économie brute annuelle</span>
          </div>
          <div className="apv2-summary-card apv2-summary-card--secondary">
            <span className="apv2-summary-value">{fmtEUR(opex.low)} – {fmtEUR(opex.high)} €</span>
            <span className="apv2-summary-label">OPEX annuel estimé (pompage, filtration)</span>
          </div>
          <div className="apv2-summary-card apv2-summary-card--secondary">
            <span className="apv2-summary-value">
              {payback ? `${payback.paybackLowYears.toFixed(1)} – ${payback.paybackHighYears.toFixed(1)} ans` : '—'}
            </span>
            <span className="apv2-summary-label">retour sur investissement</span>
          </div>
        </div>

        <p className="apv2-sim-hint" style={{ marginTop: 12 }}>
          {payback
            ? `Sur la base d'un budget de ${fmtEUR(budgetTotalLow)}–${fmtEUR(budgetTotalHigh)} €, d'une économie brute de ${fmtEUR(totalEurosSaved)} €/an (${totalWaterSavedM3.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} m³/an) et d'un OPEX annuel estimé à ${fmtEUR(opexMid)} € (électricité pompage, consommables de filtration), l'installation serait amortie en ${payback.paybackLowYears.toFixed(1)} à ${payback.paybackHighYears.toFixed(1)} ans.`
            : "Pas d'économie nette calculable pour l'instant — choisis une cuve à l'étape 5 (pour la couverture) et vérifie les usages/tarifs ci-dessus. Si l'OPEX dépasse l'économie brute, aucun retour sur investissement n'est atteint."}
        </p>
      </div>
      <div className="apv2-step-footer">
        <button type="button" className="apv2-footer-btn-ghost" onClick={goPrevious} disabled={currentStep === 0}>
          ← Précédent
        </button>
        <span />
      </div>
    </div>
  );
};

export default RoiStep;
